import React, { useMemo, useId } from "react";
import { DYAD_STATUS_PALETTE } from "./dyadConstants";
import type { ScoreHistoryPoint } from "../../services/Dyad";
import styles from "./ScoreHistoryChart.module.css";

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------

const CHART_WIDTH = 800;
const CHART_HEIGHT = 160;
const PADDING = { top: 24, right: 24, bottom: 36, left: 56 };
const PLOT_W = CHART_WIDTH - PADDING.left - PADDING.right;
const PLOT_H = CHART_HEIGHT - PADDING.top - PADDING.bottom;
const DOT_RADIUS = 6;
const SCORE_MIN = 1;
const SCORE_MAX = 5;

const SCORE_STEPS = [1, 2, 3, 4, 5] as const;
const SCORE_LABELS: Record<number, string> = {
  1: "Hostile",
  2: "Tense",
  3: "Stable",
  4: "Friendly",
  5: "Close",
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function scoreY(score: number): number {
  const ratio = (score - SCORE_MIN) / (SCORE_MAX - SCORE_MIN);
  return PADDING.top + PLOT_H * (1 - ratio);
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

function shouldShowLabel(index: number, total: number): boolean {
  if (total <= 6) return true;
  if (total <= 10) return index % 2 === 0 || index === total - 1;
  return index % 3 === 0 || index === total - 1;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface ScoreHistoryChartProps {
  data: ScoreHistoryPoint[];
  isLoading?: boolean;
  currentScore?: number;
}

export const ScoreHistoryChart: React.FC<ScoreHistoryChartProps> = ({
  data,
  isLoading,
  currentScore,
}) => {
  const gradientId = useId().replace(/:/g, "_");

  const points = useMemo(() => {
    if (data.length === 0) return [];
    if (data.length === 1) {
      return [
        {
          x: PADDING.left + PLOT_W / 2,
          y: scoreY(data[0].score),
          ...data[0],
        },
      ];
    }
    return data.map((p, i) => ({
      x: PADDING.left + (i / (data.length - 1)) * PLOT_W,
      y: scoreY(p.score),
      ...p,
    }));
  }, [data]);

  const linePath = useMemo(() => {
    if (points.length < 2) return "";
    return points
      .map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`)
      .join(" ");
  }, [points]);

  const areaPath = useMemo(() => {
    if (points.length < 2) return "";
    const baseline = PADDING.top + PLOT_H;
    return (
      `M ${points[0].x} ${baseline} ` +
      points.map((p) => `L ${p.x} ${p.y}`).join(" ") +
      ` L ${points[points.length - 1].x} ${baseline} Z`
    );
  }, [points]);

  if (isLoading) {
    return (
      <div className={styles.wrapper}>
        <p className={styles.label}>Score History</p>
        <div className={styles.loadingPlaceholder}>
          <span className={styles.loadingText}>Loading history...</span>
        </div>
      </div>
    );
  }

  if (data.length === 0 && currentScore) {
    return (
      <div className={styles.wrapper}>
        <p className={styles.label}>Score History</p>
        <div className={styles.singlePoint}>
          <span
            className={styles.singleDot}
            style={{
              backgroundColor: DYAD_STATUS_PALETTE[currentScore]?.bg,
            }}
          />
          <span className={styles.singleText}>
            {DYAD_STATUS_PALETTE[currentScore]?.label} ({currentScore}/5)
            &mdash; first assessment
          </span>
        </div>
      </div>
    );
  }

  if (data.length === 0) return null;

  return (
    <div className={styles.wrapper}>
      <p className={styles.label}>Score History</p>
      <svg
        viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
        className={styles.svg}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop
              offset="0%"
              stopColor="var(--main-blue, #4c90f0)"
              stopOpacity={0.1}
            />
            <stop
              offset="100%"
              stopColor="var(--main-blue, #4c90f0)"
              stopOpacity={0.01}
            />
          </linearGradient>
        </defs>

        {/* Grid lines + left axis text labels */}
        {SCORE_STEPS.map((s) => (
          <g key={s}>
            <line
              x1={PADDING.left}
              x2={PADDING.left + PLOT_W}
              y1={scoreY(s)}
              y2={scoreY(s)}
              stroke="var(--box-border-color, #e5e7eb)"
              strokeWidth={0.8}
              strokeDasharray={s === SCORE_MIN || s === SCORE_MAX ? "0" : "4 4"}
            />
            {/* Only show top and bottom labels to keep it clean */}
            {(s === SCORE_MIN || s === SCORE_MAX) && (
              <text
                x={PADDING.left - 10}
                y={scoreY(s) + 4}
                textAnchor="end"
                className={styles.axisLabel}
              >
                {SCORE_LABELS[s]}
              </text>
            )}
          </g>
        ))}

        {/* Area fill */}
        {areaPath && <path d={areaPath} fill={`url(#${gradientId})`} />}

        {/* Line */}
        {linePath && (
          <path
            d={linePath}
            fill="none"
            stroke="var(--main-blue, #4c90f0)"
            strokeWidth={2.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}

        {/* Data points + date labels */}
        {points.map((p, i) => {
          const pal = DYAD_STATUS_PALETTE[p.score];
          const isLast = i === points.length - 1;
          const showDate = shouldShowLabel(i, points.length);
          return (
            <g key={i}>
              {isLast && (
                <circle
                  cx={p.x}
                  cy={p.y}
                  r={DOT_RADIUS + 4}
                  fill="none"
                  stroke={pal?.bg ?? "#888"}
                  strokeWidth={1.5}
                  opacity={0.35}
                />
              )}
              <circle
                cx={p.x}
                cy={p.y}
                r={DOT_RADIUS}
                fill={pal?.bg ?? "#888"}
                stroke="var(--main-background, #fff)"
                strokeWidth={2.5}
              />
              {showDate && (
                <text
                  x={p.x}
                  y={PADDING.top + PLOT_H + 20}
                  textAnchor="middle"
                  className={styles.dateLabel}
                >
                  {formatDate(p.date)}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
};

export default ScoreHistoryChart;
